import React, { useEffect, useState } from 'react';
import {
  RiFileTextLine as DocumentIcon,
  RiDeleteBinLine as DeleteIcon,
  RiDownloadLine as DownloadIcon,
  RiEyeLine as ViewIcon,
  RiFileList2Line as PetitionIcon,
  RiFileWordLine as WordIcon,
  RiFilePdfLine as PdfIcon,
  RiCheckboxCircleLine as CheckIcon,
  RiMailLine as LetterIcon,
  RiShieldCheckLine as ShieldCheckIcon,
} from 'react-icons/ri';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Card from '@/components/ui/Card';
import PetitionService, { Petition } from '@/api/petitions';
import { ApiService } from '@/api/cases';
import { LettersApi } from '@/api/letters';
import { buildAllCriteriaMap } from '@/utils/visaConstants';
import { analytics } from '@/utils/analytics';
import { ANALYTICS_EVENTS } from '@/utils/analytics-events';

interface PetitionTabProps {
  caseId: string;
  visaType?: string;
}

const PetitionTab: React.FC<PetitionTabProps> = ({ caseId, visaType }) => {
  const [loading, setLoading] = useState(false);
  const [petitions, setPetitions] = useState<Petition[]>([]);
  const [viewingPetition, setViewingPetition] = useState<Petition | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [documentsCount, setDocumentsCount] = useState<number>(0);
  const [lettersCount, setLettersCount] = useState<number>(0);
  const [expandedPetitions, setExpandedPetitions] = useState<Set<string>>(
    new Set()
  );

  const criteriaMap = React.useMemo(() => buildAllCriteriaMap(), []);

  const togglePetitionStats = (petitionId: string) => {
    setExpandedPetitions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(petitionId)) {
        newSet.delete(petitionId);
      } else {
        newSet.add(petitionId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    loadPetitions();
    loadCaseStatistics();
  }, [caseId]);

  const loadCaseStatistics = async () => {
    try {
      // Fetch documents count - use meta.total for accurate count
      const docsResponse = await ApiService.getCaseDocuments(caseId, 1, 1);
      if (docsResponse && docsResponse.meta) {
        setDocumentsCount(docsResponse.meta.total || 0);
      } else if (docsResponse && docsResponse.items) {
        // Fallback for legacy response shape
        setDocumentsCount(docsResponse.items.length);
      }

      // Fetch letters count from Letters API
      try {
        const lettersResponse = await LettersApi.listLetters(caseId);
        setLettersCount(lettersResponse.length);
      } catch (lettersErr) {
        console.error('Failed to load letters count:', lettersErr);
        setLettersCount(0);
      }
    } catch (err) {
      console.error('Failed to load case statistics:', err);
    }
  };

  const loadPetitions = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await PetitionService.getPetitionsForCase(caseId);
      if (response.success && response.data) {
        setPetitions(
          Array.isArray(response.data) ? response.data : [response.data]
        );
      } else {
        setPetitions([]);
      }
    } catch (err: any) {
      console.error('Failed to load petitions:', err);
      setError(err.message || 'Failed to load petitions');
      setPetitions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (petitionId: string) => {
    if (!confirm('Are you sure you want to delete this petition?')) {
      return;
    }

    try {
      await PetitionService.deletePetition(petitionId);
      setSuccess('Petition deleted successfully');
      await loadPetitions();
    } catch (err: any) {
      console.error('Failed to delete petition:', err);
      setError(err.message || 'Failed to delete petition');
    }
  };

  const handleDownloadPDF = async (petitionId: string) => {
    try {
      analytics.track(ANALYTICS_EVENTS.PACKAGE_TAB_DOWNLOAD_PDF_CLICKED, {
        case_id: caseId,
        petition_id: petitionId,
      });
      setDownloading(`${petitionId}-pdf`);
      const blob = await PetitionService.downloadPetition(petitionId, 'pdf');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `petition-${petitionId.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setSuccess('Petition downloaded as PDF');
    } catch (err: any) {
      console.error('Failed to download petition:', err);
      setError(err.message || 'Failed to download petition');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadWord = async (petitionId: string) => {
    try {
      analytics.track(ANALYTICS_EVENTS.PACKAGE_TAB_DOWNLOAD_WORD_CLICKED, {
        case_id: caseId,
        petition_id: petitionId,
      });
      setDownloading(`${petitionId}-word`);
      const blob = await PetitionService.downloadForGoogleDocs(petitionId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `petition-${petitionId.substring(0, 8)}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setSuccess('Petition downloaded as DOCX');
    } catch (err: any) {
      console.error('Failed to download petition:', err);
      setError(err.message || 'Failed to download petition');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadExhibitList = async (petition: Petition) => {
    try {
      setDownloading(`${petition.id}-exhibit`);

      // Get exhibit list ID from petition metadata
      const exhibitListId = (petition.metadata as any)?.exhibitListId;

      if (!exhibitListId) {
        setError('No exhibit list found for this petition');
        setDownloading(null);
        return;
      }

      const blob = await ApiService.downloadExhibitList(
        caseId,
        exhibitListId,
        'pdf'
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `exhibit-list-${exhibitListId.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setSuccess('Exhibit list downloaded as PDF');
    } catch (err: any) {
      console.error('Failed to download exhibit list:', err);
      setError(err.message || 'Failed to download exhibit list');
    } finally {
      setDownloading(null);
    }
  };

  const handleView = async (petitionId: string) => {
    try {
      const response = await PetitionService.getPetition(petitionId);
      if (response.success && response.data) {
        setViewingPetition(response.data);
      }
    } catch (err: any) {
      console.error('Failed to load petition:', err);
      setError(err.message || 'Failed to load petition details');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
      case 'done':
        return 'text-success-600 bg-success-50';
      case 'generating':
      case 'in_progress':
        return 'text-primary-600 bg-primary-50';
      case 'failed':
        return 'text-error-600 bg-error-50';
      case 'draft':
        return 'text-neutral-600 bg-neutral-50';
      default:
        return 'text-neutral-600 bg-neutral-50';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
      case 'done':
        return 'Completed';
      case 'generating':
        return 'Generating...';
      case 'in_progress':
        return 'In Progress';
      case 'failed':
        return 'Failed';
      case 'draft':
        return 'Draft';
      default:
        return status;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Date not available';
      }
      return date.toLocaleString();
    } catch {
      return 'Date not available';
    }
  };

  const isCompleted = (status: string) => {
    return status === 'completed' || status === 'done';
  };

  const getCriteriaForPetition = (petition: Petition): string[] => {
    // Extract criteria from petition metadata or content
    const criteria: string[] = (petition.metadata as any)?.criteria || [];
    return criteria;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (petitions.length === 0) {
    return (
      <div className="space-y-6">
        {error && (
          <Card padding="sm">
            <div className="text-sm text-error-600">{error}</div>
          </Card>
        )}

        <Card padding="lg">
          <div className="text-center py-12">
            <div className="flex flex-col items-center space-y-4">
              <div className="h-16 w-16 rounded-full bg-primary-100 flex items-center justify-center">
                <PetitionIcon className="h-8 w-8 text-primary-600" />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-semibold text-neutral-900">
                  No Petitions Yet
                </h3>
                <p className="text-neutral-500 text-sm max-w-md mx-auto">
                  Go to the Drafting tab to generate a new petition with AI.
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Render petitions list
  return (
    <div className="space-y-6">
      {/* Messages */}
      {error && (
        <Card padding="sm">
          <div className="text-sm text-error-600">{error}</div>
        </Card>
      )}
      {success && (
        <Card padding="sm">
          <div className="text-sm text-success-600">{success}</div>
        </Card>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-neutral-900">
          Petitions ({petitions.length})
        </h2>
      </div>

      {/* Petitions List */}
      <div className="space-y-4">
        {petitions.map(petition => {
          const criteria = getCriteriaForPetition(petition);

          return (
            <Card key={petition.id} padding="md">
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <PetitionIcon className="h-5 w-5 text-primary-600" />
                      <h3 className="text-base font-semibold text-neutral-900">
                        {petition.petitionType || 'Immigration Petition'}
                      </h3>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(petition.status)}`}
                      >
                        {getStatusLabel(petition.status)}
                      </span>
                    </div>
                    <div className="text-sm text-neutral-500 space-y-1">
                      <p>Generated: {formatDate(petition.createdAt)}</p>
                      {petition.metadata?.beneficiaryName && (
                        <p>Beneficiary: {petition.metadata.beneficiaryName}</p>
                      )}
                      {petition.metadata?.progress !== undefined &&
                        petition.status === 'generating' && (
                          <p>
                            Progress:{' '}
                            {Math.round(petition.metadata.progress * 100)}%
                          </p>
                        )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(petition.id)}
                    className="flex items-center space-x-1 text-error-600 hover:text-error-700"
                  >
                    <DeleteIcon className="h-4 w-4" />
                  </Button>
                </div>

                {/* Criteria Met */}
                {criteria.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-neutral-700 mb-2">
                      Criteria Met:
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {criteria.map(criterionId => (
                        <span
                          key={criterionId}
                          className="px-2 py-1 rounded-md text-xs bg-primary-50 text-primary-700 border border-primary-200"
                        >
                          {criteriaMap.get(criterionId) || criterionId}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Statistics Toggle */}
                <div className="border-t border-neutral-200 pt-3">
                  <button
                    onClick={() => togglePetitionStats(petition.id)}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    {expandedPetitions.has(petition.id)
                      ? '− Hide Statistics'
                      : '+ Show Statistics'}
                  </button>
                </div>

                {/* Expandable Statistics */}
                {expandedPetitions.has(petition.id) && (
                  <div className="space-y-4 border-t border-neutral-200 pt-4">
                    {/* Status Overview */}
                    <div>
                      <h4 className="text-sm font-semibold text-neutral-900 mb-3">
                        Status Overview
                      </h4>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <div
                            className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center ${
                              documentsCount > 0
                                ? 'bg-success-500 border-success-500'
                                : 'border-neutral-300'
                            }`}
                          >
                            {documentsCount > 0 && (
                              <svg
                                className="w-3 h-3 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={3}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            )}
                          </div>
                          <span className="text-sm text-neutral-700">
                            Documents -{' '}
                            <span className="font-medium">
                              {documentsCount} uploaded
                            </span>
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div
                            className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center ${
                              petition.content?.coverLetter ||
                              petition.content?.cover_letter
                                ? 'bg-success-500 border-success-500'
                                : 'border-neutral-300'
                            }`}
                          >
                            {(petition.content?.coverLetter ||
                              petition.content?.cover_letter) && (
                              <svg
                                className="w-3 h-3 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={3}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            )}
                          </div>
                          <span className="text-sm text-neutral-700">
                            Cover Letter -{' '}
                            <span className="font-medium">
                              {petition.content?.coverLetter ||
                              petition.content?.cover_letter
                                ? 'Completed'
                                : 'Not completed'}
                            </span>
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div
                            className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center ${
                              lettersCount > 0
                                ? 'bg-success-500 border-success-500'
                                : 'border-neutral-300'
                            }`}
                          >
                            {lettersCount > 0 && (
                              <svg
                                className="w-3 h-3 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={3}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            )}
                          </div>
                          <span className="text-sm text-neutral-700">
                            Expert Letters -{' '}
                            <span className="font-medium">
                              {lettersCount} created
                            </span>
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div
                            className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center ${
                              isCompleted(petition.status)
                                ? 'bg-success-500 border-success-500'
                                : 'border-neutral-300'
                            }`}
                          >
                            {isCompleted(petition.status) && (
                              <svg
                                className="w-3 h-3 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={3}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            )}
                          </div>
                          <span className="text-sm text-neutral-700">
                            Ready Status -{' '}
                            <span className="font-medium">
                              {isCompleted(petition.status)
                                ? 'Ready'
                                : 'In progress'}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Section Completion */}
                    {petition.content?.sections &&
                      (() => {
                        const sections = petition.content.sections as any;
                        const sectionItems = [
                          { key: 'cover_letter', label: 'Main Section' },
                          { key: 'introduction', label: 'Introduction' },
                          {
                            key: 'professional_experience',
                            label: 'Professional Experience',
                          },
                          {
                            key: 'domain_expertise',
                            label: 'Domain Expertise',
                          },
                          { key: 'education', label: 'Education' },
                          {
                            key: 'benefits_intro',
                            label: 'Benefits Introduction',
                          },
                          {
                            key: 'field_significance',
                            label: 'Field Significance',
                          },
                          {
                            key: 'expected_contribution',
                            label: 'Expected Contribution',
                          },
                          { key: 'conclusions', label: 'Conclusions' },
                          ...(petition.petitionType === 'EB-1A' ||
                          visaType === 'EB-1A'
                            ? [{ key: 'work_plans', label: 'Work Plans' }]
                            : []),
                        ];

                        const totalSections = sectionItems.length;
                        const completedCount = sectionItems.filter(
                          item => sections && sections[item.key]?.content
                        ).length;
                        const overallPercentage = Math.round(
                          (completedCount / totalSections) * 100
                        );

                        return (
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-sm font-semibold text-neutral-900">
                                Section Completion
                              </h4>
                              <span className="text-sm font-semibold text-primary-600">
                                {overallPercentage}%
                              </span>
                            </div>
                            <div className="space-y-2">
                              {sectionItems.map(item => {
                                const isComplete =
                                  sections && sections[item.key]?.content;
                                return (
                                  <div
                                    key={item.key}
                                    className="flex items-center space-x-2"
                                  >
                                    <div
                                      className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center ${
                                        isComplete
                                          ? 'bg-success-500 border-success-500'
                                          : 'border-neutral-300'
                                      }`}
                                    >
                                      {isComplete && (
                                        <svg
                                          className="w-3 h-3 text-white"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={3}
                                            d="M5 13l4 4L19 7"
                                          />
                                        </svg>
                                      )}
                                    </div>
                                    <span
                                      className={`text-sm ${isComplete ? 'text-neutral-700' : 'text-neutral-500'}`}
                                    >
                                      {item.label}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                  </div>
                )}

                {/* Download Actions */}
                <div className="flex flex-wrap gap-2 pt-2 border-t border-neutral-200">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadPDF(petition.id)}
                    loading={downloading === `${petition.id}-pdf`}
                    disabled={!!downloading}
                    className="flex items-center space-x-2"
                  >
                    <PdfIcon className="h-4 w-4" />
                    <span>Download PDF</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadWord(petition.id)}
                    loading={downloading === `${petition.id}-word`}
                    disabled={!!downloading}
                    className="flex items-center space-x-2"
                  >
                    <WordIcon className="h-4 w-4" />
                    <span>Download Word</span>
                  </Button>
                  {(petition.metadata as any)?.exhibitListId && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadExhibitList(petition)}
                      loading={downloading === `${petition.id}-exhibit`}
                      disabled={!!downloading}
                      className="flex items-center space-x-2"
                    >
                      <DownloadIcon className="h-4 w-4" />
                      <span>Download Exhibit List</span>
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleView(petition.id)}
                    className="flex items-center space-x-2"
                  >
                    <ViewIcon className="h-4 w-4" />
                    <span>View</span>
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Petition View Modal */}
      {viewingPetition && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-neutral-900">
                {viewingPetition.petitionType || 'Immigration Petition'}
              </h2>
              <button
                onClick={() => setViewingPetition(null)}
                className="text-neutral-500 hover:text-neutral-700"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-6">
                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-neutral-700">
                      Status:
                    </span>
                    <span
                      className={`ml-2 px-2 py-1 rounded-full text-xs ${getStatusColor(viewingPetition.status)}`}
                    >
                      {getStatusLabel(viewingPetition.status)}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-neutral-700">
                      Generated:
                    </span>
                    <span className="ml-2 text-neutral-600">
                      {formatDate(viewingPetition.createdAt)}
                    </span>
                  </div>
                  {viewingPetition.metadata?.beneficiaryName && (
                    <div>
                      <span className="font-medium text-neutral-700">
                        Beneficiary:
                      </span>
                      <span className="ml-2 text-neutral-600">
                        {viewingPetition.metadata.beneficiaryName}
                      </span>
                    </div>
                  )}
                </div>

                {/* Petition Content */}
                {viewingPetition.content?.petition && (
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 mb-3">
                      Petition
                    </h3>
                    <div className="prose prose-sm max-w-none bg-neutral-50 rounded-lg p-6">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {viewingPetition.content.petition}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}

                {/* Cover Letter */}
                {(viewingPetition.content?.cover_letter ||
                  viewingPetition.content?.coverLetter) && (
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 mb-3">
                      Cover Letter
                    </h3>
                    <div className="prose prose-sm max-w-none bg-neutral-50 rounded-lg p-6">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {viewingPetition.content.cover_letter ||
                          viewingPetition.content.coverLetter}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}

                {/* Work Plans - only for EB-1A */}
                {(viewingPetition.petitionType === 'EB-1A' ||
                  visaType === 'EB-1A') &&
                  (viewingPetition.content?.work_plans ||
                    viewingPetition.content?.workPlans) && (
                    <div>
                      <h3 className="text-lg font-semibold text-neutral-900 mb-3">
                        Work Plans
                      </h3>
                      <div className="prose prose-sm max-w-none bg-neutral-50 rounded-lg p-6">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {viewingPetition.content.work_plans ||
                            viewingPetition.content.workPlans}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}

                {/* Sections */}
                {viewingPetition.content?.sections &&
                  Object.keys(viewingPetition.content.sections).length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-neutral-900 mb-3">
                        Sections
                      </h3>
                      {Object.entries(viewingPetition.content.sections).map(
                        ([key, value]) => {
                          // Handle both old format (string) and new format (object with content property)
                          const content =
                            typeof value === 'string'
                              ? value
                              : (value as any)?.content || '';
                          const title =
                            typeof value === 'string'
                              ? key.replace(/_/g, ' ')
                              : (value as any)?.title || key.replace(/_/g, ' ');

                          return (
                            <div key={key} className="mb-4">
                              <h4 className="font-medium text-neutral-800 mb-2 capitalize">
                                {title}
                              </h4>
                              <div className="prose prose-sm max-w-none bg-neutral-50 rounded-lg p-6">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                  {content}
                                </ReactMarkdown>
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  )}

                {/* No Content */}
                {!viewingPetition.content?.petition &&
                  !viewingPetition.content?.cover_letter &&
                  !viewingPetition.content?.coverLetter &&
                  !viewingPetition.content?.work_plans &&
                  !viewingPetition.content?.workPlans &&
                  (!viewingPetition.content?.sections ||
                    Object.keys(viewingPetition.content.sections).length ===
                      0) && (
                    <div className="text-center py-8 text-neutral-500">
                      No content available for this petition yet.
                    </div>
                  )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-neutral-200 flex justify-between">
              <Button variant="ghost" onClick={() => setViewingPetition(null)}>
                Close
              </Button>
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={() => handleDownloadPDF(viewingPetition.id)}
                  loading={downloading === `${viewingPetition.id}-pdf`}
                  disabled={!!downloading}
                >
                  Download PDF
                </Button>
                <Button
                  onClick={() => handleDownloadWord(viewingPetition.id)}
                  loading={downloading === `${viewingPetition.id}-word`}
                  disabled={!!downloading}
                >
                  Download Word
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PetitionTab;
